import React, { createContext, useContext, useMemo } from 'react'
import { FlagProvider as UnleashSdkProvider } from '@unleash/proxy-client-react'
import { AppLoader } from 'shared/AppLoader'
import { CONFIG } from 'shared/modules/featureFlagsConfig'
import { getFeatureFlagProvider } from './providersByFlag'
import {
  GrowthbookProvider,
  useGrowthbookFeatureValue,
  useGrowthbookStatus,
} from './GrowthbookProvider'
import {
  UnleashProvider,
  useUnleashFeatureValue,
  useUnleashStatus,
} from './UnleashProvider'
import {
  BackendProvider,
  useBackendFeatureLoader,
  useBackendFeatureValue,
  useBackendStatus,
} from './BackendProvider'

const FeatureFlagsRuntimeContext = createContext({
  isReady: false,
  getFlagValue: () => false,
  loadFlagValue: () => {},
})

const FeatureFlagsRuntimeStateProvider = ({ children }) => {
  const growthbookReady = useGrowthbookStatus()
  const unleashReady = useUnleashStatus()
  const backendReady = useBackendStatus()
  const getGrowthbookFeatureValue = useGrowthbookFeatureValue()
  const getUnleashFeatureValue = useUnleashFeatureValue()
  const getBackendFeatureValue = useBackendFeatureValue()
  const loadBackendFeatureValue = useBackendFeatureLoader()

  const value = useMemo(
    () => ({
      isReady: unleashReady && growthbookReady && backendReady,
      getFlagValue: (flagName, options = {}) => {
        const provider = options.provider || getFeatureFlagProvider(flagName)

        if (provider === 'growthbook') {
          return Boolean(getGrowthbookFeatureValue(flagName, false))
        }

        if (provider === 'backend') {
          return Boolean(
            getBackendFeatureValue(flagName, options.context, false)
          )
        }

        return getUnleashFeatureValue(flagName)
      },
      loadFlagValue: (flagName, options = {}) => {
        const provider = options.provider || getFeatureFlagProvider(flagName)

        if (provider === 'backend') {
          loadBackendFeatureValue(flagName, options.context)
        }
      },
    }),
    [
      backendReady,
      getBackendFeatureValue,
      getGrowthbookFeatureValue,
      getUnleashFeatureValue,
      growthbookReady,
      loadBackendFeatureValue,
      unleashReady,
    ]
  )

  return (
    <FeatureFlagsRuntimeContext.Provider value={value}>
      {children}
    </FeatureFlagsRuntimeContext.Provider>
  )
}

export const useFeatureFlagsRuntime = () =>
  useContext(FeatureFlagsRuntimeContext)

const FeatureFlagsRuntimeBoundary = ({ children }) => {
  const { isReady } = useFeatureFlagsRuntime()

  return isReady ? children : <AppLoader />
}

export const FeatureFlagsRuntimeProvider = ({
  userId,
  unleashProxy,
  growthbook,
  featureFlagsBackend = {},
  children,
}) => (
  <UnleashSdkProvider
    config={{ ...CONFIG, ...unleashProxy, context: { userId } }}
  >
    <UnleashProvider>
      <BackendProvider evaluateUrl={featureFlagsBackend.evaluateUrl}>
        <GrowthbookProvider
          apiHost={growthbook.apiHost}
          clientKey={growthbook.clientKey}
          userId={userId}
        >
          <FeatureFlagsRuntimeStateProvider>
            <FeatureFlagsRuntimeBoundary>
              {children}
            </FeatureFlagsRuntimeBoundary>
          </FeatureFlagsRuntimeStateProvider>
        </GrowthbookProvider>
      </BackendProvider>
    </UnleashProvider>
  </UnleashSdkProvider>
)
